/* eslint-disable class-methods-use-this */
/* eslint-disable @typescript-eslint/member-ordering */

import { logger } from '@accedo/logger';
import { CacheService } from '##/services/cache';
import { SubscriptionService } from '##/services/elevate/types';
import { Entitlement, Subscription } from '##/dataModels/subscription';
import { getNZOSBaseUrl } from '##/services/nzos/config';

// Logger prefix
const LP = '[NzosRentalService]';

const DEFAULT_TTL_SECONDS = 60; // 1 minute
const SHORT_TTL = DEFAULT_TTL_SECONDS * 10; // 10 minutes

const subscriptionCache = CacheService.createCache({
  cacheType: 'localStorage',
  prefix: 'nzos-subscription',
  ttlSeconds: SHORT_TTL,
  automaticCleanup: true,
  flushOnStart: true,
});

/**
 * NZOS Rental Entitlement response from API.
 */
export interface RentalEntitlement {
  assetId: string;
  itemId?: string; // OVP item identifier from bulk entitlements response
  entitled: boolean;
  rentalType?: 'rental' | 'purchase';
  expiresAt?: number; // Unix timestamp
  timeLeftHours?: number;
  playbackStarted?: boolean;
  firstPlayedAt?: number | null; // Unix timestamp
  rentedAt?: number; // Unix timestamp
}

/**
 * NZOS Rental Service implementing rental/entitlement functionality.
 * Uses NZOS middleware rental endpoints. Implements SubscriptionService interface for compatibility.
 */
class NzosRentalService implements SubscriptionService {
  // eslint-disable-next-line no-use-before-define
  private static instance: NzosRentalService;

  // Track in-flight requests to prevent duplicate API calls
  private inflightRequest: Promise<RentalEntitlement[]> | null = null;

  private constructor() {
    logger.debug(`${LP} Creating new instance of NzosRentalService`);
  }

  static getInstance(): NzosRentalService {
    if (!NzosRentalService.instance) {
      NzosRentalService.instance = new NzosRentalService();
    }
    return NzosRentalService.instance;
  }

  /**
   * Clear all cached subscription and entitlement data.
   */
  async clearSubscriptionsCache(): Promise<void> {
    logger.info(`${LP} clearSubscriptionsCache called`);
    await subscriptionCache.clear();
  }

  /**
   * Add or update a rental entitlement in the cache.
   * Used after successful rental to immediately update cached data without refetching.
   * @param authToken - Bearer authentication token.
   * @param newEntitlement - The rental entitlement to add/update.
   */
  async updateRentalEntitlementCache(authToken: string, newEntitlement: RentalEntitlement): Promise<void> {
    const cacheKey = `rental-entitlements-${authToken.substring(0, 10)}`;

    try {
      // Get existing cached entitlements
      const cachedEntitlements = await subscriptionCache.get<RentalEntitlement[]>(cacheKey) || [];

      // Check if this entitlement already exists (by assetId)
      const existingIndex = cachedEntitlements.findIndex(ent => ent.assetId === newEntitlement.assetId);

      if (existingIndex >= 0) {
        // Update existing entitlement
        cachedEntitlements[existingIndex] = newEntitlement;
        logger.debug(`${LP} Updated existing rental entitlement in cache for asset ${newEntitlement.assetId}`);
      } else {
        // Add new entitlement
        cachedEntitlements.push(newEntitlement);
        logger.debug(`${LP} Added new rental entitlement to cache for asset ${newEntitlement.assetId}`);
      }

      // Save updated cache
      await subscriptionCache.set(cacheKey, cachedEntitlements, SHORT_TTL);
    } catch (error) {
      logger.error(`${LP} Error updating rental entitlement cache:`, error);
    }
  }

  /**
   * Convert NZOS RentalEntitlement to Subscription model.
   * NZOS uses rental/purchase model, mapped to subscription for compatibility.
   */
  private mapRentalToSubscription(rental: RentalEntitlement): Subscription {
    const now = Date.now();
    const startDate = rental.rentedAt ? new Date(rental.rentedAt * 1000) : undefined;
    const endDate = rental.expiresAt ? new Date(rental.expiresAt * 1000) : undefined;

    // Determine status based on entitlement and expiration
    let status: 'active' | 'expired' | 'inactive' = 'inactive';
    if (rental.entitled) {
      if (rental.expiresAt && rental.expiresAt * 1000 > now) {
        status = 'active';
      } else if (rental.expiresAt) {
        status = 'expired';
      } else {
        // No expiration means permanent purchase
        status = 'active';
      }
    }

    return {
      orderId: `rental-${rental.assetId}`,
      userId: undefined, // NZOS doesn't provide userId in rental response
      sku: rental.assetId,
      status,
      startDate,
      endDate,
      renewable: false, // Rentals are not renewable
      plan: rental.rentalType
        ? {
            sku: rental.assetId,
            site: 'nzos',
            active: rental.entitled,
            name: rental.rentalType === 'rental' ? 'Content Rental' : 'Content Purchase',
            description:
              rental.rentalType === 'rental'
                ? `Rental access for ${rental.assetId}`
                : `Purchased content ${rental.assetId}`,
            interval: {
              type: rental.rentalType === 'rental' ? 'hour' : 'lifetime',
              length: rental.timeLeftHours || 0,
              amount: {
                currencyCode: 'NZD',
                value: 0, // NZOS doesn't provide pricing in entitlement response
              },
            },
            autoRenew: false,
          }
        : undefined,
    };
  }

  /**
   * Convert NZOS RentalEntitlement to Entitlement model.
   */
  private mapRentalToEntitlement(rental: RentalEntitlement): Entitlement {
    const category = rental.rentalType === 'purchase' ? 'TVOD' : 'RENTAL';
    const details = rental.rentalType === 'purchase' ? 'PURCHASE' : 'RENTAL';

    return {
      category,
      details,
      value: rental.assetId,
      extensions: {
        entitled: rental.entitled,
        expiresAt: rental.expiresAt,
        timeLeftHours: rental.timeLeftHours,
        playbackStarted: rental.playbackStarted,
        firstPlayedAt: rental.firstPlayedAt,
        rentedAt: rental.rentedAt,
        rentalType: rental.rentalType,
      },
    };
  }

  /**
   * Private method to fetch raw rental entitlements from API.
   * This is used by both getSubscriptions and getEntitlements to share cache.
   * Prevents duplicate API calls by reusing in-flight requests.
   * @param authToken - Bearer authentication token.
   * @returns Array of raw RentalEntitlement objects.
   */
  private async fetchRentalEntitlements(authToken: string): Promise<RentalEntitlement[]> {
    const baseUrl = getNZOSBaseUrl();
    const endpoint = '/rentals/entitlements';
    const cacheKey = `rental-entitlements-${authToken.substring(0, 10)}`;

    // Check cache first
    const cachedResponse = await subscriptionCache.get<RentalEntitlement[]>(cacheKey);
    if (cachedResponse) {
      logger.debug(`${LP} Returning cached rental entitlements`);
      return cachedResponse;
    }

    // If there's already a request in flight, wait for it
    if (this.inflightRequest) {
      logger.debug(`${LP} Waiting for in-flight request to complete`);
      return this.inflightRequest;
    }

    // Create new request
    logger.debug(`${LP} Fetching rental entitlements from API`);
    this.inflightRequest = (async () => {
      try {
        const response = await fetch(`${baseUrl}${endpoint}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          logger.error(`${LP} Failed to get rental entitlements: ${response.status} ${errorText}`);
          throw new Error(`Failed to get rental entitlements: ${response.status}`);
        }

        const data = await response.json();
        // The bulk endpoint returns `itemId` (required) as the OVP item identifier
        // and `assetId` (optional/nullable). Normalise so assetId is always set,
        // allowing all downstream code that keys maps by assetId to work correctly.
        const rentals: RentalEntitlement[] = (data.items || []).map(
          (item: RentalEntitlement & { itemId?: string }) => ({
            ...item,
            assetId: item.assetId ?? item.itemId ?? '',
          }),
        );
        logger.debug(`${LP} Retrieved ${rentals.length} rental entitlements`);

        // Cache the raw results
        await subscriptionCache.set(cacheKey, rentals, SHORT_TTL);

        return rentals;
      } finally {
        // Clear in-flight request
        this.inflightRequest = null;
      }
    })();

    return this.inflightRequest;
  }

  /**
   * Public method to get raw rental entitlements.
   * Used by components that need RentalEntitlement[] format instead of mapped Entitlement[].
   * Shares cache and in-flight request handling with getSubscriptions/getEntitlements.
   * @param params - The parameters.
   * @param params.authToken - Bearer authentication token.
   * @returns Array of raw RentalEntitlement objects.
   */
  async getRawRentalEntitlements({ authToken }: { authToken: string }): Promise<RentalEntitlement[]> {
    logger.debug(`${LP} getRawRentalEntitlements called`);
    return this.fetchRentalEntitlements(authToken);
  }

  /**
   * Get all rental entitlements for the authenticated user.
   * Maps to subscriptions for compatibility with existing SubscriptionService interface.
   * @param params - The parameters.
   * @param params.authToken - Bearer authentication token.
   * @returns Array of subscriptions (mapped from NZOS rentals).
   */
  async getSubscriptions({ authToken }: { authToken: string }): Promise<Subscription[]> {
    logger.debug(`${LP} getSubscriptions called`);

    try {
      const rentals = await this.fetchRentalEntitlements(authToken);
      return rentals.map((rental) => this.mapRentalToSubscription(rental));
    } catch (error) {
      logger.error(`${LP} Error retrieving subscriptions:`, error);
      throw error;
    }
  }

  /**
   * Get all rental entitlements for the authenticated user.
   * @param params - The parameters.
   * @param params.authToken - Bearer authentication token.
   * @returns Array of entitlements (mapped from NZOS rentals).
   */
  async getEntitlements({ authToken }: { authToken: string }): Promise<Entitlement[]> {
    logger.debug(`${LP} getEntitlements called`);

    try {
      const rentals = await this.fetchRentalEntitlements(authToken);
      return rentals.map((rental) => this.mapRentalToEntitlement(rental));
    } catch (error) {
      logger.error(`${LP} Error retrieving entitlements:`, error);
      throw error;
    }
  }

  /**
   * Check if user has rental entitlement for a specific asset.
   * This is an additional helper method specific to NZOS rental model.
   * @param params - The parameters.
   * @param params.authToken - Bearer authentication token.
   * @param params.assetId - Asset identifier to check entitlement for.
   * @returns RentalEntitlement for the specific asset.
   */
  async getEntitlementByAsset({
    authToken,
    assetId,
  }: {
    authToken: string;
    assetId: string;
  }): Promise<RentalEntitlement> {
    logger.debug(`${LP} getEntitlementByAsset called for assetId: ${assetId}`);

    const baseUrl = getNZOSBaseUrl();
    const endpoint = `/rentals/entitlement/${assetId}`;
    const cacheKey = `entitlement-${assetId}-${authToken.substring(0, 10)}`;

    // Check cache first
    const cachedResponse = await subscriptionCache.get<RentalEntitlement>(cacheKey);
    if (cachedResponse) {
      logger.debug(`${LP} Returning cached entitlement for asset ${assetId}`);
      return cachedResponse;
    }

    try {
      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error(`${LP} Failed to get entitlement for asset ${assetId}: ${response.status} ${errorText}`);
        throw new Error(`Failed to get entitlement for asset ${assetId}: ${response.status}`);
      }

      const rental: RentalEntitlement = await response.json();
      logger.debug(`${LP} Retrieved entitlement for asset ${assetId}: entitled=${rental.entitled}`);

      // Cache the result
      await subscriptionCache.set(cacheKey, rental, SHORT_TTL);

      return rental;
    } catch (error) {
      logger.error(`${LP} Error retrieving entitlement for asset ${assetId}:`, error);
      throw error;
    }
  }
}

export { NzosRentalService };
